import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import LiveStatusBadge from '../products/Common/live/LiveStatusBadge'
import { liveDashboardUrl, useLiveEmbed } from '../products/Common/live/useLiveEmbed'
import { useCurrentSite } from '../store/store.hooks'
import { useSiteLiveDashboards } from './useSiteLiveDashboards'

const BAR_HEIGHT = 40

const centered = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  justifyContent: 'center',
  alignItems: 'center',
} as const

/**
 * Full-page cloud dashboard, reached from the sidebar's Dashboards section (`/dashboards/:dashboardId`).
 * Only dashboards listed for the current site are shown: switching site away from it shows a notice.
 */
const LiveDashboardPage = () => {
  const { t } = useTranslation()
  const { dashboardId } = useParams<{ dashboardId: string }>()
  const currentSite = useCurrentSite()
  // isLoading, not isPending: a disabled query (no live app configured) stays pending forever.
  const { data: dashboards, isLoading } = useSiteLiveDashboards()
  const dashboard = dashboards?.find((d) => d.Id === dashboardId)

  if (isLoading) {
    return (
      <Box sx={centered}>
        <CircularProgress color="secondary" />
      </Box>
    )
  }

  if (!currentSite || !dashboard) {
    return (
      <Box sx={{ ...centered, opacity: 0.6 }}>
        <Typography align="center" variant="h6">
          {t('dashboards.live.notFound')}
        </Typography>
      </Box>
    )
  }

  // key: a new dashboard or site gets a new iframe and a fresh handshake.
  return (
    <LiveDashboardFrame
      key={`${currentSite.id}/${dashboard.Id}`}
      siteId={currentSite.id}
      dashboardId={dashboard.Id}
      title={dashboard.Name}
    />
  )
}

const LiveDashboardFrame = ({ siteId, dashboardId, title }: { siteId: string; dashboardId: string; title: string }) => {
  const { t } = useTranslation()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const src = liveDashboardUrl(siteId, dashboardId, { embed: true })
  const state = useLiveEmbed(iframeRef, src)

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 2,
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ height: BAR_HEIGHT, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        <LiveStatusBadge state={state} />
        <Button
          startIcon={<OpenInNewIcon />}
          href={liveDashboardUrl(siteId, dashboardId, { embed: false })}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          color="primary"
          sx={{ fontSize: '0.7rem' }}
        >
          {t('dashboards.live.openInNewTab')}
        </Button>
      </Box>
      <Box sx={{ flexGrow: 1, minHeight: 0, backgroundColor: '#1e1e1e', borderRadius: '6px', overflow: 'hidden' }}>
        <iframe ref={iframeRef} src={src} title={title} style={{ width: '100%', height: '100%', border: 0 }} />
      </Box>
    </Box>
  )
}

export default LiveDashboardPage
